
import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGetHarvestsByFarmId } from '@/hooks/use-harvest';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface HarvestSummaryProps {
  farmId: string;
  estimatedHarvestKg: number | null;
}

const HarvestSummary: React.FC<HarvestSummaryProps> = ({ farmId, estimatedHarvestKg }) => {
  const navigate = useNavigate();
  const { data: harvests, isLoading, error } = useGetHarvestsByFarmId(farmId);

  // Calculate total harvested quantity
  const totalHarvested = React.useMemo(() => {
    if (!harvests) return 0;
    return harvests
      .filter(harvest => harvest.status === 'completed')
      .reduce((sum, harvest) => sum + (harvest.actual_quantity || 0), 0);
  }, [harvests]);

  // Calculate harvest progress
  const harvestProgress = React.useMemo(() => {
    if (!estimatedHarvestKg || estimatedHarvestKg === 0) return 0;
    const progress = (totalHarvested / estimatedHarvestKg) * 100;
    return Math.min(progress, 100); // Cap at 100%
  }, [totalHarvested, estimatedHarvestKg]);

  // Count harvests by status
  const harvestCounts = React.useMemo(() => {
    if (!harvests) return { planned: 0, in_progress: 0, completed: 0, cancelled: 0 };
    
    return harvests.reduce((counts, harvest) => {
      counts[harvest.status]++;
      return counts;
    }, { planned: 0, in_progress: 0, completed: 0, cancelled: 0 });
  }, [harvests]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-3/4 mb-2" />
          <Skeleton className="h-4 w-1/2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-6 w-full mb-4" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Harvest Summary</CardTitle>
          <CardDescription>Error loading harvests</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-red-500">Failed to load harvest data</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Harvest Summary</CardTitle>
        <CardDescription>Overview of harvest progress</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Total Harvested</span>
            <span className="font-medium">{totalHarvested.toLocaleString()} kg</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Target Quantity</span>
            <span className="font-medium">{estimatedHarvestKg ? estimatedHarvestKg.toLocaleString() : 'Not set'} kg</span>
          </div>
          <Progress value={harvestProgress} className="h-2" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Progress</span>
            <span>{harvestProgress.toFixed(1)}%</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 bg-blue-50 rounded-md">
            <p className="text-blue-700 text-xs font-medium mb-1">Planned</p>
            <p className="text-2xl font-bold text-blue-800">{harvestCounts.planned}</p>
          </div>
          <div className="p-3 bg-amber-50 rounded-md">
            <p className="text-amber-700 text-xs font-medium mb-1">In Progress</p>
            <p className="text-2xl font-bold text-amber-800">{harvestCounts.in_progress}</p>
          </div>
          <div className="p-3 bg-green-50 rounded-md">
            <p className="text-green-700 text-xs font-medium mb-1">Completed</p>
            <p className="text-2xl font-bold text-green-800">{harvestCounts.completed}</p>
          </div>
          <div className="p-3 bg-red-50 rounded-md">
            <p className="text-red-700 text-xs font-medium mb-1">Cancelled</p>
            <p className="text-2xl font-bold text-red-800">{harvestCounts.cancelled}</p>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          variant="ghost" 
          className="w-full flex justify-between items-center"
          onClick={() => navigate(`/harvests?farmId=${farmId}`)}
        >
          <span>View All Harvests</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};

export default HarvestSummary;
